"use client";

// arena-chat-panel.tsx
// The bottom-sheet that opens when tapping the online-count in the arena.
// Merges what used to be two separate things: the spectator roster
// (arena-spectator-modal.tsx, now retired) and the group chat (formerly its
// own /chat page). Two tabs in one popup:
//   - "Online": who's watching, online/away status, and a "Tell" action that
//     jumps to the Chat tab with "@DisplayName " pre-filled — a shortcut into
//     the existing @mention pipeline (notification + ticker chime), not a
//     separate notification mechanism.
//   - "Chat": the group chat feed + composer. A leading "/shout " in the
//     composer sends via /api/shout (breaking-news ticker broadcast) instead
//     of a normal chat message — replaces the old standalone ShoutBar button.
//
// Chat messages/likes/deletes/mentions arrive over the SAME arena SSE
// connection live-arena.tsx already holds (system channel) — this component
// has no EventSource of its own. live-arena.tsx forwards those events in via
// props instead of rendering its old full-screen trophy/breaking-news
// takeover while this panel is open (see the chatOpen-gated branches there).

import { useEffect, useRef, useState } from "react";
import { clog } from "@/lib/client-log";
import { useDoubleTap } from "@/lib/use-double-tap";
import { sendChatMessage, toggleChatLike, deleteChatMessage } from "../actions/chat";
import { CHAT_MESSAGE_MAX_LENGTH, type ChatMessageView } from "@/lib/chat-types";
import { SHOUT_MAX_LENGTH } from "@/lib/shout-constants";
import { SpectatorSprite } from "./arena-sprite";
import type { Spectator } from "./arena-sprite";

interface MentionCandidate { id: string; displayName: string; }
type ConnStatus = "connected" | "reconnecting" | "disconnected";

export function ArenaChatPanel({
  spectators,
  messages,
  currentUserId,
  isAdmin,
  mentionCandidates,
  initialTab = "online",
  connStatus = "connected",
  onChatTabViewed,
  onClose,
}: {
  spectators: Spectator[];
  messages: ChatMessageView[];
  currentUserId: string;
  isAdmin: boolean;
  mentionCandidates: MentionCandidate[];
  initialTab?: "online" | "chat";
  connStatus?: ConnStatus;
  onChatTabViewed?: () => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<"online" | "chat">(initialTab);
  const [composerPrefill, setComposerPrefill] = useState<string | null>(null);

  useEffect(() => {
    if (tab === "chat") onChatTabViewed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function tellUser(displayName: string) {
    setComposerPrefill(`@${displayName} `);
    setTab("chat");
  }

  const onlineCount = spectators.filter((s) => !s.away).length;
  const awayCount = spectators.length - onlineCount;

  return (
    <div
      className="fixed inset-0 z-[30] flex items-end justify-center"
      style={{ touchAction: "manipulation" }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-sm rounded-t-2xl overflow-hidden flex flex-col"
        style={{
          background: "linear-gradient(180deg, #2d1060 0%, #1e0b4a 100%)",
          border: "1px solid rgba(196,181,253,0.25)",
          borderBottom: "none",
          height: "75vh",
          maxHeight: "75vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-white/30" />
        </div>

        {/* Header / tabs */}
        <div className="flex items-center justify-between px-4 pb-2 shrink-0">
          <div className="flex gap-1.5 bg-white/5 rounded-full p-1">
            <button
              onClick={() => setTab("online")}
              className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wide transition-colors ${
                tab === "online" ? "bg-purple-600 text-white" : "text-white/50"
              }`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5" />
              {spectators.length} Online
            </button>
            <button
              onClick={() => setTab("chat")}
              className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wide transition-colors ${
                tab === "chat" ? "bg-purple-600 text-white" : "text-white/50"
              }`}
            >
              💬 Chat
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white text-xl leading-none px-1"
            style={{ touchAction: "manipulation" }}
          >×</button>
        </div>

        {tab === "online" ? (
          <OnlineTab spectators={spectators} onlineCount={onlineCount} awayCount={awayCount} onTell={tellUser} />
        ) : (
          <ChatTab
            messages={messages}
            currentUserId={currentUserId}
            isAdmin={isAdmin}
            mentionCandidates={mentionCandidates}
            prefill={composerPrefill}
            onPrefillConsumed={() => setComposerPrefill(null)}
            connStatus={connStatus}
          />
        )}
      </div>
    </div>
  );
}

// ── Online tab ─────────────────────────────────────────────────────────────────

function OnlineTab({
  spectators, onlineCount, awayCount, onTell,
}: {
  spectators: Spectator[]; onlineCount: number; awayCount: number; onTell: (displayName: string) => void;
}) {
  const [openFor, setOpenFor] = useState<string | null>(null);

  return (
    <div className="flex-1 overflow-y-auto min-h-0">
      <div className="px-5 pb-2 text-white/45 font-semibold text-xs">
        {onlineCount} online{awayCount > 0 ? ` · ${awayCount} away` : ""} · tap someone to say hi
      </div>
      {spectators.length === 0 ? (
        <div className="text-center text-white/40 text-sm py-8">No one else is here yet</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {spectators.map((s) => (
            <li key={s.userId}>
              <button
                onClick={() => setOpenFor(openFor === s.userId ? null : s.userId)}
                className="w-full flex items-center gap-3 px-5 py-3 text-left"
                style={{ opacity: s.away ? 0.55 : 1, touchAction: "manipulation" }}
              >
                <div className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md" style={{ background: "rgba(109,40,217,0.4)" }}>
                  {s.spriteUri
                    ? <SpectatorSprite uri={s.spriteUri} animMeta={s.animMeta} size={28} displayName={s.displayName} />
                    : <div className="w-5 h-5 rounded-sm" style={{ background: "#6d28d9" }} />
                  }
                </div>
                <span className="text-white/90 font-semibold text-sm truncate flex-1">{s.displayName || "Anonymous"}</span>
                {s.away ? (
                  <span className="shrink-0 flex items-center gap-1 text-purple-300/70 font-semibold text-xs uppercase tracking-wide">💤 Away</span>
                ) : (
                  <span className="shrink-0 flex items-center gap-1.5 text-green-300/80 font-semibold text-xs uppercase tracking-wide">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Online
                  </span>
                )}
              </button>
              {openFor === s.userId && (
                <div className="px-5 pb-3 -mt-1">
                  <button
                    onClick={() => onTell(s.displayName)}
                    className="rounded-full bg-purple-600/80 hover:bg-purple-600 text-white font-bold text-xs px-4 py-1.5"
                    style={{ touchAction: "manipulation" }}
                  >
                    💬 Tell {s.displayName || "them"}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Chat tab ───────────────────────────────────────────────────────────────────

function ChatTab({
  messages, currentUserId, isAdmin, mentionCandidates, prefill, onPrefillConsumed, connStatus,
}: {
  messages: ChatMessageView[];
  currentUserId: string;
  isAdmin: boolean;
  mentionCandidates: MentionCandidate[];
  prefill: string | null;
  onPrefillConsumed: () => void;
  connStatus: ConnStatus;
}) {
  const disconnected = connStatus !== "connected";
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (prefill === null) return;
    setBody(prefill);
    onPrefillConsumed();
    textareaRef.current?.focus();
  }, [prefill, onPrefillConsumed]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const isShoutCommand = body.startsWith("/shout ") || body === "/shout";
  const maxLen = isShoutCommand ? SHOUT_MAX_LENGTH + 7 : CHAT_MESSAGE_MAX_LENGTH;

  function handleBodyChange(value: string) {
    setBody(value.slice(0, maxLen));
    if (value.startsWith("/")) { setMentionQuery(null); return; }
    const cursorAt = value.lastIndexOf("@");
    if (cursorAt === -1) { setMentionQuery(null); return; }
    setMentionQuery(value.slice(cursorAt + 1));
  }

  function insertMention(displayName: string) {
    const cursorAt = body.lastIndexOf("@");
    if (cursorAt === -1) return;
    setBody(body.slice(0, cursorAt) + "@" + displayName + " ");
    setMentionQuery(null);
    textareaRef.current?.focus();
  }

  const mentionMatches = mentionQuery
    ? mentionCandidates.filter((u) => u.displayName.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 5)
    : [];

  async function send() {
    const trimmed = body.trim();
    if (!trimmed || sending || disconnected) return;
    setSending(true);
    try {
      if (trimmed.startsWith("/shout ") || trimmed === "/shout") {
        const shoutBody = trimmed.slice("/shout".length).trim();
        if (!shoutBody) { clog.warn("arena", "empty /shout, ignoring"); return; }
        const res = await fetch("/api/shout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: shoutBody }),
        });
        const data = await res.json();
        if (!res.ok) { clog.warn("arena", "shout send rejected", { error: data.error }); return; }
      } else {
        const result = await sendChatMessage(trimmed);
        if (!result.ok) { clog.warn("chat", "send rejected", { error: result.error }); return; }
      }
      setBody("");
      setMentionQuery(null);
    } catch (err) {
      clog.warn("chat", "send failed", { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-4 pt-2 space-y-3 pb-3 min-h-0">
        {messages.length === 0 && (
          <p className="text-purple-300/70 text-sm text-center py-8">No messages yet — say hi! 👋</p>
        )}
        {messages.map((m) => (
          <ChatBubble
            key={m.id}
            message={m}
            isOwn={m.userId === currentUserId}
            isAdmin={isAdmin}
            onLike={() => toggleChatLike(m.id).catch((err) => clog.warn("chat", "like failed", { error: String(err) }))}
            onDelete={() => deleteChatMessage(m.id).catch((err) => clog.warn("chat", "delete failed", { error: String(err) }))}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      <div
        className="relative px-4 pt-2 border-t border-white/10 shrink-0"
        style={{ paddingBottom: "calc(1rem + var(--safe-area-inset-bottom))" }}
      >
        {mentionMatches.length > 0 && (
          <div className="absolute left-4 right-4 bottom-full mb-1 bg-[#2d1060] rounded-xl shadow-lg border border-white/10 overflow-hidden">
            {mentionMatches.map((u) => (
              <button
                key={u.id}
                onClick={() => insertMention(u.displayName)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-white/10 font-semibold text-purple-200"
              >
                @{u.displayName}
              </button>
            ))}
          </div>
        )}
        {disconnected ? (
          <div className="text-yellow-300/80 text-xs font-bold mb-1.5 flex items-center gap-1">
            {connStatus === "reconnecting" ? "⏳ Reconnecting… chat will resume shortly" : "✕ Disconnected — chat is paused"}
          </div>
        ) : isShoutCommand && (
          <div className="text-red-300 text-xs font-bold mb-1.5 flex items-center gap-1">
            📣 Sending as a shout — everyone&apos;s ticker will show this
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => handleBodyChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder="Message the group, or /shout ..."
            rows={1}
            disabled={disconnected}
            className="flex-1 rounded-xl border border-white/15 bg-white/10 text-white placeholder:text-white/30 px-3 py-2 text-sm resize-none focus:outline-none focus:border-purple-400 disabled:opacity-50"
          />
          <button
            onClick={send}
            disabled={sending || !body.trim() || disconnected}
            className={`rounded-xl font-bold text-sm px-4 py-2 disabled:opacity-40 shrink-0 text-white ${
              isShoutCommand ? "bg-red-600" : "bg-purple-600"
            }`}
          >
            {sending ? "…" : isShoutCommand ? "Shout" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({
  message, isOwn, isAdmin, onLike, onDelete,
}: {
  message: ChatMessageView; isOwn: boolean; isAdmin: boolean; onLike: () => void; onDelete: () => void;
}) {
  const [optimisticLiked, setOptimisticLiked] = useState(message.likedByMe);
  const [optimisticCount, setOptimisticCount] = useState(message.likeCount);

  useEffect(() => {
    setOptimisticLiked(message.likedByMe);
    setOptimisticCount(message.likeCount);
  }, [message.likedByMe, message.likeCount]);

  const handleTap = useDoubleTap(
    () => { /* single tap on a bubble does nothing */ },
    () => {
      setOptimisticLiked((v) => !v);
      setOptimisticCount((c) => (optimisticLiked ? Math.max(0, c - 1) : c + 1));
      onLike();
    }
  );

  if (message.deleted) {
    return <div className="text-xs text-purple-300/50 italic px-2">message removed by an admin</div>;
  }

  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[80%]">
        {!isOwn && <div className="text-xs font-bold text-purple-300 mb-0.5 px-1">{message.displayName}</div>}
        <div
          onClick={handleTap}
          className={`rounded-2xl px-3 py-2 text-sm cursor-pointer select-none ${
            isOwn ? "bg-purple-600 text-white" : "bg-white/10 text-white border border-white/10"
          }`}
          style={{ WebkitUserSelect: "none", userSelect: "none" }}
          title="Double-tap to like"
        >
          {message.body}
        </div>
        <div className="flex items-center gap-2 mt-0.5 px-1">
          {optimisticCount > 0 && (
            <span className="text-xs text-pink-300 font-bold">❤️ {optimisticCount}</span>
          )}
          {isAdmin && (
            <button onClick={onDelete} className="text-xs text-red-300/70 hover:text-red-300">
              delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
